import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquareText, Upload, FileText, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { TypeBadge, SeverityBadge, ConfidenceMeter } from "../components/Badges";

const STAGES = [
  { key: "uploaded", label: "Uploaded" },
  { key: "reading", label: "Reading pages" },
  { key: "analyzing", label: "AI analyzing" },
  { key: "photos", label: "Extracting photos" },
  { key: "done", label: "Done" },
];

const UploadProgress = ({ stage }) => {
  const idx = STAGES.findIndex((s) => s.key === stage);
  return (
    <div data-testid="upload-progress" className="rounded-lg border border-indigo-300 dark:border-indigo-500/40 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-slate-200">
        <Loader2 className="h-4 w-4 animate-spin text-indigo-600 dark:text-indigo-400" />
        Processing your document — you can wait here or check Events shortly.
      </div>
      <ol className="space-y-1.5">
        {STAGES.map((s, i) => (
          <li
            key={s.key}
            data-testid={`upload-stage-${s.key}`}
            className={`flex items-center gap-2 text-sm font-mono uppercase tracking-wider ${
              i < idx
                ? "text-emerald-600 dark:text-emerald-400"
                : i === idx
                  ? "text-indigo-600 dark:text-indigo-400 font-bold"
                  : "text-slate-400 dark:text-slate-600"
            }`}
          >
            {i < idx ? <CheckCircle2 className="h-3.5 w-3.5" /> : i === idx ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="h-3.5 w-3.5" />}
            {s.label}
          </li>
        ))}
      </ol>
    </div>
  );
};

const ParsedPreview = ({ event, onConfirm, confirming }) => (
  <div data-testid="parse-preview" className="rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-white dark:bg-slate-900 p-4 space-y-3">
    <div className="flex flex-wrap items-center gap-2">
      <TypeBadge type={event.event_type} />
      <SeverityBadge severity={event.severity} note={event.impact_note} />
      <ConfidenceMeter value={event.confidence} />
    </div>
    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{event.title}</h3>
    <p className="text-base text-slate-600 dark:text-slate-400">{event.summary}</p>
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
      <span className="font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400">{event.vendor_name}</span>
      {event.due_date && <span className="font-mono text-cyan-600 dark:text-cyan-400">due {event.due_date}</span>}
    </div>
    {event.recommended_action && (
      <div className="rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-base text-slate-700 dark:text-slate-300">
        <span className="font-mono text-sm uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block mb-0.5">Recommended action</span>
        {event.recommended_action}
      </div>
    )}
    <div className="flex flex-wrap items-center gap-2">
      {event.needs_confirm ? (
        <button
          data-testid="parse-confirm-btn"
          onClick={onConfirm}
          disabled={confirming}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-2 text-base font-bold text-emerald-950 hover:bg-emerald-400 transition-colors disabled:opacity-50"
        >
          <CheckCircle2 className="h-4 w-4" /> {confirming ? "Confirming…" : "Confirm & publish"}
        </button>
      ) : (
        <span data-testid="parse-auto-approved" className="inline-flex items-center gap-1.5 rounded-md border border-emerald-400 dark:border-emerald-500/50 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="h-3.5 w-3.5" /> Auto-classified — high confidence, already live
        </span>
      )}
    </div>
  </div>
);

const Capture = () => {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState(null);
  const [failed, setFailed] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const parseText = async () => {
    if (!text.trim()) return;
    setParsing(true);
    setParsed(null);
    try {
      const r = await api.post("/capture/text", { text: text.trim(), source: "whatsapp_paste" });
      setParsed(r.data.event);
      toast.success("AI parsed the forward");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setParsing(false);
    }
  };

  const pollCapture = (captureId) => {
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/captures/${captureId}`);
        const cap = r.data.capture;
        setStage(cap.stage || "uploaded");
        if (cap.status === "parsed" && r.data.event) {
          clearInterval(pollRef.current);
          setUploading(false);
          setStage(null);
          setParsed(r.data.event);
          toast.success("Parsed — review below");
        } else if (cap.status === "failed") {
          clearInterval(pollRef.current);
          setUploading(false);
          setStage(null);
          setFailed(cap.error || "AI could not parse this file");
          toast.error("Parsing failed");
        }
      } catch {
        /* keep polling */
      }
    }, 3000);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setParsed(null);
    setFailed(null);
    setStage("uploaded");
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/capture/file", form, { headers: { "Content-Type": "multipart/form-data" } });
      pollCapture(r.data.capture_id);
    } catch (e) {
      toast.error(formatApiError(e));
      setUploading(false);
      setStage(null);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmParsed = async () => {
    if (!parsed) return;
    setConfirming(true);
    try {
      await api.post(`/events/${parsed.id}/confirm`, {});
      toast.success("Event confirmed — live in feed & brief");
      navigate(`/events/${parsed.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div data-testid="capture-page" className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Capture</h1>
        <p className="text-base text-slate-600 dark:text-slate-400">
          Paste a WhatsApp forward or drop a PDF / image / Excel. The AI turns it into a Business Event.
        </p>
      </div>

      <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 space-y-3" data-testid="whatsapp-capture-panel">
        <div className="flex items-center gap-2">
          <MessageSquareText className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">WhatsApp forward</h2>
        </div>
        <textarea
          data-testid="whatsapp-pastebox-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder="Paste a WhatsApp forward from a brand rep here…"
          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-2.5 text-base text-slate-900 dark:text-slate-100 placeholder:text-slate-600 dark:placeholder:text-slate-400 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
        />
        <div className="flex items-center gap-2">
          <button
            data-testid="parse-whatsapp-submit-btn"
            onClick={parseText}
            disabled={parsing || !text.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-500 px-4 py-2 text-base font-bold text-emerald-950 hover:bg-emerald-400 transition-colors disabled:opacity-50"
          >
            {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {parsing ? "AI parsing…" : "Analyze with AI"}
          </button>
          <span className="text-sm text-slate-600 dark:text-slate-400">Twilio auto-forward goes live in a later phase — this box feeds the same pipeline.</span>
        </div>
      </div>

      <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 space-y-3" data-testid="file-capture-panel">
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">PDF / Image / Excel</h2>
        </div>
        <label
          data-testid="file-dropzone"
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-4 py-10 text-center hover:border-emerald-400 dark:hover:border-emerald-500/50 transition-colors"
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-emerald-600 dark:text-emerald-400" />
          ) : (
            <FileText className="h-6 w-6 text-slate-600 dark:text-slate-400" />
          )}
          <span className="text-base text-slate-600 dark:text-slate-400">
            {uploading ? "Upload received — AI is working…" : "Click to choose a price list PDF, scheme image or ledger Excel"}
          </span>
          <span className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">pdf · png · jpg · webp · xls · xlsx · csv</span>
          <input
            ref={fileRef}
            data-testid="file-upload-input"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.xls,.xlsx,.csv,.txt"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0])}
          />
        </label>
      </div>

      {uploading && stage && <UploadProgress stage={stage} />}

      {failed && (
        <div data-testid="upload-failed" className="rounded-lg border border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/10 p-4 flex items-start gap-2">
          <XCircle className="h-5 w-5 text-rose-600 dark:text-rose-400 mt-0.5" />
          <div>
            <div className="text-base font-semibold text-rose-700 dark:text-rose-300">Could not parse this file</div>
            <div className="text-sm text-rose-600/80 dark:text-rose-400/80">{failed} — the file is saved in your Document Library; try again or paste the text above.</div>
          </div>
        </div>
      )}

      {parsed && <ParsedPreview event={parsed} onConfirm={confirmParsed} confirming={confirming} />}
    </div>
  );
};

export default Capture;
